import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Box,
    IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { CalendarEventDto, CalendarEventType } from '../../types/calendar';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { toLocalIso } from '@/utils/datetime';

interface EventEditModalProps {
    open: boolean;
    eventData: Partial<CalendarEventDto> | null;
    eventTypes: CalendarEventType[];
    onClose: () => void;
    onSave: (event: Partial<CalendarEventDto & { eventTypeId?: number }>) => void;
    onDelete: (eventId: number) => void;
}

const toDateTimePickerFormat = (isoString: string | undefined | null) => {
    if (!isoString) return '';
    try {
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '';
        return toLocalIso(date).slice(0, 16);
    } catch (e) {
        return '';
    }
};

export const EventEditModal: React.FC<EventEditModalProps> = ({ open, eventData, eventTypes, onClose, onSave, onDelete }) => {
    const [formData, setFormData] = useState<Partial<CalendarEventDto & { eventTypeId?: number }>>({});

    useEffect(() => {
        if (eventData) {
            setFormData({
                ...eventData,
                eventTypeId: eventData.eventType?.id,
            });
        } else {
            setFormData({});
        }
    }, [eventData]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setFormData((prev: Partial<CalendarEventDto & { eventTypeId?: number }>) => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
        }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData((prev: Partial<CalendarEventDto & { eventTypeId?: number }>) => ({
            ...prev,
            [name]: checked, // Set isCompleted to true or false directly
        }));
    };
    
    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (value) {
            const isoString = new Date(value).toLocaleString('sv-SE').replace(' ', 'T').slice(0, 19);
            setFormData((prev: Partial<CalendarEventDto & { eventTypeId?: number }>) => ({ ...prev, [name]: isoString }));
        } else {
            setFormData((prev: Partial<CalendarEventDto & { eventTypeId?: number }>) => ({ ...prev, [name]: null }));
        }
    };

    const handleSave = () => {
        onSave(formData);
    };

    const handleDelete = () => {
        if (formData.id) {
            onDelete(formData.id);
        }
    };

    const isNewEvent = !formData.id;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" data-i-doc={ModalIndices.EVENT_EDIT}>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {isNewEvent ? 'Створити нову подію' : 'Редагувати подію'}
                    {!isNewEvent && (
                        <IconButton onClick={handleDelete} color="error">
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3} sx={{ pt: 2 }}>
                    <Grid item xs={12}>
                        <TextField
                            select
                            required
                            name="eventTypeId"
                            label="Тип події"
                            value={formData.eventTypeId || ''}
                            onChange={handleChange}
                            fullWidth
                            disabled={!isNewEvent}
                        >
                            {eventTypes.map(type => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            name="title"
                            label="Назва події"
                            value={formData.title || ''}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Grid>
                    {/* Removed description field */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            name="scheduledLdt"
                            label="Дата та час запланованої події"
                            type="datetime-local"
                            value={toDateTimePickerFormat(formData.scheduledLdt)}
                            onChange={handleDateChange}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            name="energyImpact"
                            label="Вплив на енергію (ккал)"
                            type="number"
                            value={formData.energyImpact || ''}
                            onChange={handleChange}
                            fullWidth
                        />
                    </Grid>
                    {!isNewEvent && (
                         <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={!!formData.isCompleted} onChange={handleCheckboxChange} name="isCompleted" />}
                                label="Подію виконано"
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Скасувати</Button>
                <Button onClick={handleSave} variant="contained" color="primary">Зберегти</Button>
            </DialogActions>
        </Dialog>
    );
};
